/**
 * The awards API — whether a course CAN certify anyone, and what one learner actually did.
 *
 * `GET /api/awards/v1/courses/<course_key>/verdict` answers a question none of the platform's
 * own endpoints answer directly: given the grading policy as saved, can ANY learner reach the
 * pass mark? `CourseGrading.tsx` lets an author set a weight, a `min_count` and a `drop_count`
 * per assignment type without checking they can multiply out to a passing grade — the platform
 * accepts a policy where a course's graded content maxes out below its own cutoff, silently. A
 * course discovered this way after certificates were promised is the failure mode `grading:
 * 'misconfigured'` exists to catch before that conversation happens.
 *
 * `GET …/learners/<username>/answers` and its `/…/<block_key>` sibling exist for the opposite
 * problem: an admin who can already see a SCORE has no way to see what the learner actually
 * submitted, and — more often the reason for the question — no way to tell "opened it" from
 * "answered it". MEASURED on this deployment: 17 of 19 survey rows hold `{"choices": null}`,
 * because a row is created when the block is viewed, not when it is answered. Collapsing those
 * two into one state is exactly the bug an admin drilling in is trying to rule out, so this file
 * carries THREE states end to end and never reduces them to a boolean.
 *
 * NO FAN-OUT. `AdminGrades.tsx` (see its header) measured 31.69s against 2.60s for the same 37
 * enrolments once one screen started computing a full grade summary per row instead of reading
 * what the list endpoint already carried. The index endpoint here is the same shape of trap: it
 * is ONE request per learner per course, built once, and nothing below loops the per-block
 * endpoint over a subsection or a course. A block's answer is fetched only when a human expands
 * that exact row, and the caller is expected to cache it — `LearnerAnswers.tsx` does, with an
 * infinite `staleTime`, because a learner's already-submitted answer does not change out from
 * under an open page the way a grade in progress might.
 */

import { openedx } from './openedx';

const course = (courseKey: string) => encodeURIComponent(courseKey);
const BASE = (courseKey: string) => `/api/awards/v1/courses/${course(courseKey)}`;

/* ------------------------------------------------------------------------ verdict ---- */

export type GradingState = 'graded' | 'ungraded' | 'misconfigured';

export type VerdictReason =
  | 'reachable_cutoff'
  | 'no_graded_subsection'
  | 'graded_subsections_score_nothing'
  | 'graded_content_carries_no_weight'
  | 'no_positive_cutoff'
  | 'max_achievable_below_cutoff';

/** One assignment type's share of the arithmetic, from the policy as saved. */
export interface VerdictBreakdownRow {
  assignment_type: string;
  /** Fraction 0..1, AS A STRING — see `fractionPercent`. */
  weight: string;
  min_count: number;
  drop_count: number;
  /** How many subsections of this type the course actually has. */
  present: number;
  denominator: number;
  /** Fraction 0..1, AS A STRING, this type can contribute at most. */
  max_contribution: string;
}

/**
 * Whether this course's grading policy can pass anyone, computed server-side against the
 * policy as saved — not the form's draft. `pass_cutoff` and `max_achievable_percent` are
 * FRACTIONS 0..1 AS STRINGS, matching the rest of the grading surface (`GradingModel`'s
 * cutoffs are the same shape); `fractionPercent` below is what turns either into a number
 * worth printing. `arithmetic` and `warning_empty_graded_subsections` are present only when
 * they apply — an absent field is not the same as an empty one here.
 */
export interface CourseVerdict {
  api_version: string;
  course_key: string;
  grading: GradingState;
  max_achievable_percent: string | null;
  pass_cutoff: string | null;
  reason: VerdictReason;
  structure_version: string;
  empty_graded_subsections: string[];
  breakdown: VerdictBreakdownRow[];
  /** The sentence to show verbatim when a course is graded or ungraded — see AC-045. */
  certificate_rule: string;
  /** Present ONLY when `grading === 'misconfigured'`: one line per assignment type. */
  arithmetic?: string[];
  /** Present only when `empty_graded_subsections` is non-empty. */
  warning_empty_graded_subsections?: string;
}

export const getCourseVerdict = (courseKey: string) =>
  openedx.get<CourseVerdict>(`${BASE(courseKey)}/verdict`);

/**
 * A payload fraction (0..1, arriving as a STRING) as a whole-percent number for display.
 *
 * `null`/`undefined`/unparsable all stay `null` — never 0. `parseFloat(null)` reads as `NaN`
 * in principle, but the whole point of this helper is that nobody downstream has to remember
 * that: a missing cutoff is "not applicable", and rendering it as 0% would tell an author their
 * course requires a perfect score of nothing, which is not what an absent cutoff means.
 */
export function fractionPercent(fraction: string | null | undefined): number | null {
  if (fraction === null || fraction === undefined) return null;
  const value = Number.parseFloat(fraction);
  return Number.isFinite(value) ? Math.round(value * 1000) / 10 : null;
}

/* -------------------------------------------------------------------------- answers ---- */

/**
 * FOUR states, and they are not a spectrum. Use the payload's own `legend` strings to label
 * them — see the file header for why "seen" is not allowed to read as "answered" anywhere in
 * this UI.
 *
 * `undetermined` is the one that was missing, and its absence was a live defect rather than a
 * gap: with only three states the server had to force every block type it could not read into
 * one of them, and it chose `seen` — "they did not answer" — on a truthiness test of a blob
 * whose shape it did not know. MEASURED: the one learner on the deployment who has ever
 * uploaded a file was reported that way, because Staff Graded Assignment keeps `{}` in the
 * courseware state table and files the real submission somewhere else entirely.
 *
 * So `undetermined` means: thinlms will not claim either way here. It is NOT a softer `seen`,
 * and it must never be styled or worded as one — a row an admin reads as "no answer" is
 * exactly the failure this state exists to prevent.
 */
export type AnswerState = 'not_seen' | 'seen' | 'answered' | 'undetermined';

/** One block's status within a subsection — the index view, before any answer is fetched. */
export interface AnswerBlockSummary {
  block_key: string;
  block_type: string;
  title: string;
  state: AnswerState;
  /** Whether expanding this row can show a real answer, or only a state and a link out. */
  decodable: boolean;
  score_earned: number | null;
  score_possible: number | null;
  max_score: number | null;
  weight: number | null;
  last_activity: string | null;
}

export interface AnswerSubsection {
  block_key: string;
  title: string;
  graded: boolean;
  assignment_type: string | null;
  blocks: AnswerBlockSummary[];
}

/**
 * ONE learner's course, as states only. Deliberately carries no answer content — see the file
 * header — so this is the cheap request the drill-in can afford to make on every open, and the
 * per-block endpoint below is the one guarded behind an explicit click.
 */
export interface LearnerAnswersIndex {
  api_version: string;
  course_key: string;
  username: string;
  subsections: AnswerSubsection[];
  /** The platform's own wording for each state — shown to the admin verbatim, never rewritten. */
  legend: Record<AnswerState, string>;
  /**
   * Whether the submissions tables — where file uploads and open responses actually live —
   * could be read on this request. `false` means every block type that files its work there is
   * `undetermined` for that reason and not because the learner did nothing.
   */
  submissions_consulted: boolean;
}

export const getLearnerAnswers = (courseKey: string, username: string) =>
  openedx.get<LearnerAnswersIndex>(
    `${BASE(courseKey)}/learners/${encodeURIComponent(username)}/answers`,
  );

export interface ProblemAnswer {
  kind: 'problem';
  /** Keyed by input id — shape is the problem's own, not something to interpret here. */
  answers: Record<string, unknown>;
  attempts: number | null;
  correct_map: Record<string, 'correct' | 'incorrect' | 'partially-correct' | null>;
}

export interface PollAnswer {
  kind: 'poll';
  choice: string;
}

export interface SurveyAnswer {
  kind: 'survey';
  choices: Record<string, string>;
}

/**
 * A Staff Graded Assignment upload. THE RECORD OF A FILE, NOT THE FILE — there is no download
 * route in thinlms today, and the bytes behind an older upload may not exist at all (the media
 * volume was unmounted container-local disk until 2026-08-29, so anything written before then
 * was destroyed on the next pod restart with the database row left behind). So this is rendered
 * as evidence that a submission happened, with its name and type, and never as a link.
 */
export interface FileUploadAnswer {
  kind: 'file_upload';
  filename: string | null;
  mimetype: string | null;
  finalized: boolean;
  submitted_at: string | null;
  attempt_number: number | null;
}

/** An Open Response submission: what they wrote, and what they attached. */
export interface OpenAssessmentAnswer {
  kind: 'openassessment';
  parts: string[];
  files: { filename: string | null; description: string | null; size: number | null }[];
  submitted_at: string | null;
  attempt_number: number | null;
}

export type BlockAnswer =
  | ProblemAnswer
  | PollAnswer
  | SurveyAnswer
  | FileUploadAnswer
  | OpenAssessmentAnswer;

/**
 * One block's answer, fetched only once its row is expanded.
 *
 * `answer` is `null` for a block that is `not_seen` or `seen`-but-not-answered — there is
 * nothing decoded to show, and that is itself the fact worth displaying, not an error.
 * `submission_history_url` and `note` appear ONLY when the block was answered but this endpoint
 * could not decode it (an XBlock type it does not know how to read); they are absent otherwise,
 * so their presence is the signal, not their content.
 *
 * `submission_history_url` is a THINLMS path (`/api/awards/v1/…/submission-history/…`), not the
 * platform's `/courses/…/submission_history/…`. The platform's view gates on
 * `has_access(user, 'staff', course)`, which is true for every tenant admin on every course on
 * this install; thinlms's applies the same tenant gate as this endpoint. The frontend renders
 * whatever the server sends, so this note is a statement about the server's contract — do not
 * build the URL here.
 */
export interface LearnerBlockAnswer {
  api_version: string;
  course_key: string;
  block_key: string;
  block_type: string;
  username: string;
  state: AnswerState;
  score_earned: number | null;
  score_possible: number | null;
  last_activity: string | null;
  answer: BlockAnswer | null;
  submission_history_url?: string;
  note?: string;
  submissions_consulted: boolean;
}

export const getLearnerBlockAnswer = (courseKey: string, username: string, blockKey: string) =>
  openedx.get<LearnerBlockAnswer>(
    `${BASE(courseKey)}/learners/${encodeURIComponent(username)}/answers/${encodeURIComponent(blockKey)}`,
  );
